(() => {
  'use strict';

  const TAG_NAME = 'yamuri-sticky-purchase';
  const ACTIVE_CLASS = 'yamuri-sticky-purchase-active';
  const MOBILE_QUERY = '(max-width: 767.98px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const activeBars = new Set();

  if (!window.customElements || window.customElements.get(TAG_NAME)) return;

  const requestFrame = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : (callback) => window.setTimeout(callback, 16);
  const cancelFrame = window.cancelAnimationFrame
    ? window.cancelAnimationFrame.bind(window)
    : window.clearTimeout.bind(window);

  function updateDocumentOffset() {
    const root = document.documentElement;
    if (!activeBars.size) {
      root.classList.remove(ACTIVE_CLASS);
      root.style.removeProperty('--ym-sticky-purchase-offset');
      return;
    }

    let height = 0;
    activeBars.forEach((bar) => {
      if (bar.isConnected) height = Math.max(height, Math.ceil(bar.getBoundingClientRect().height));
    });
    root.style.setProperty('--ym-sticky-purchase-offset', `${height || 77}px`);
    root.classList.add(ACTIVE_CLASS);
  }

  class YamuriStickyPurchase extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._destroyed = false;
      this._abortController = new AbortController();
      this._scopeMedia = window.matchMedia(MOBILE_QUERY);
      this._reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this._scope = this.dataset.scope === 'mobile' ? 'mobile' : 'all';
      this._sourceVisible = false;
      this._hasPassedSource = false;
      this._footerVisible = false;
      this._footerStateReady = false;
      this._visible = false;
      this._pendingHide = false;
      this._loading = false;
      this._sawLegacyLoading = false;
      this._hasSynced = false;
      this._lastAnnouncement = '';

      this._resolveElements();
      if (!this._productRoot || !this._form || !this._source || !this._primaryButton || !this._submitButton) {
        this._hide(true);
        return;
      }

      this._bindEvents();
      this._bindVariantEvents();
      this._createObservers();
      this._syncVariant();
      this._updateVisibility();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (!this._connected) return;
      this._destroyed = true;
      this._connected = false;

      if (this._syncFrame) cancelFrame(this._syncFrame);
      if (this._showFrame) cancelFrame(this._showFrame);
      window.clearTimeout(this._hideTimer);
      window.clearTimeout(this._loadingTimer);
      this._abortController?.abort();
      this._sourceObserver?.disconnect();
      this._footerObserver?.disconnect();
      this._primaryObserver?.disconnect();
      this._bodyObserver?.disconnect();
      this._removeMediaListener(this._scopeMedia, this._handleEnvironmentChange);
      this._removeMediaListener(this._reducedMotionMedia, this._handleEnvironmentChange);

      if (this._jqueryRoot && this._jqueryNamespace) {
        this._jqueryRoot.off(this._jqueryNamespace);
      }
      if (this._jqueryBody && this._jqueryNamespace) {
        this._jqueryBody.off(this._jqueryNamespace);
      }

      this._hide(true);
    }

    _resolveElements() {
      const sectionId = String(this.dataset.sectionId || '');
      this._productRoot = Array.from(
        document.querySelectorAll('[data-section-type="product-template"][data-section-id]')
      ).find((element) => String(element.dataset.sectionId) === sectionId);

      const form = document.getElementById(this.dataset.formId || '');
      this._form = form && this._productRoot?.contains(form) ? form : null;
      this._source = this._productRoot?.querySelector('[data-yamuri-primary-purchase-anchor]') || null;
      this._primaryButton = this._productRoot?.querySelector('[data-yamuri-primary-add]') || null;
      this._variantSelect = this._form?.querySelector(`.js-product-select--${sectionId}`) || null;
      this._primaryPrice = this._productRoot?.querySelector('.js-product-price') || null;
      this._submitButton = this.querySelector('[data-yamuri-sticky-submit]');
      this._submitLabel = this.querySelector('[data-yamuri-sticky-submit-label]');
      this._price = this.querySelector('[data-yamuri-sticky-price]');
      this._variant = this.querySelector('[data-yamuri-sticky-variant]');
      this._status = this.querySelector('[data-yamuri-sticky-status]');
      this._image = this.querySelector('.yamuri-sticky-purchase__image');
      this._media = this.querySelector('.yamuri-sticky-purchase__media');
    }

    _bindEvents() {
      const signal = this._abortController.signal;
      this._handleEnvironmentChange = this._updateVisibility.bind(this);
      this._handleRootChange = () => this._scheduleSync();
      this._handleFormSubmit = () => this._setLoading(true);
      this._handleSubmitClick = (event) => {
        event.preventDefault();
        if (this._loading || !this._isPrimaryPurchasable()) return;
        this._primaryButton.click();
      };
      this._handleFocusOut = () => {
        if (!this._pendingHide) return;
        requestFrame(() => {
          if (!this.contains(document.activeElement)) {
            this._pendingHide = false;
            this._updateVisibility();
          }
        });
      };
      this._handleSectionUnload = (event) => {
        if (event.target?.contains(this) || event.target?.contains(this._productRoot)) this.destroy();
      };

      this._productRoot.addEventListener('change', this._handleRootChange, { signal });
      this._form.addEventListener('submit', this._handleFormSubmit, { signal });
      this._submitButton.addEventListener('click', this._handleSubmitClick, { signal });
      this.addEventListener('focusout', this._handleFocusOut, { signal });
      document.addEventListener('visibilitychange', this._handleEnvironmentChange, { signal });
      document.addEventListener('shopify:section:unload', this._handleSectionUnload, { signal });
      window.addEventListener('pageshow', this._handleEnvironmentChange, { signal });
      window.addEventListener('resize', this._handleEnvironmentChange, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', this._handleEnvironmentChange, { signal, passive: true });
      this._addMediaListener(this._scopeMedia, this._handleEnvironmentChange);
      this._addMediaListener(this._reducedMotionMedia, this._handleEnvironmentChange);
    }

    _bindVariantEvents() {
      if (!window.jQuery) return;
      const namespaceId = String(this.dataset.sectionId || '').replace(/[^a-zA-Z0-9_-]/g, '');
      this._jqueryNamespace = `.yamuriStickyPurchase${namespaceId}`;
      this._jqueryRoot = window.jQuery(this._productRoot);
      this._jqueryBody = window.jQuery(document.body);
      this._jqueryRoot.on(`variantChange${this._jqueryNamespace}`, (event) => {
        this._scheduleSync(event.variant, true);
      });
      this._jqueryBody.on(`ajaxCart.afterCartLoad${this._jqueryNamespace}`, () => {
        this._setLoading(false);
      });
    }

    _createObservers() {
      if ('IntersectionObserver' in window) {
        this._sourceObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          this._sourceVisible = entry.isIntersecting;
          this._hasPassedSource = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this._updateVisibility();
        }, { threshold: 0 });
        this._sourceObserver.observe(this._source);

        const footer = document.querySelector('footer, #footer');
        if (footer) {
          this._footerObserver = new IntersectionObserver((entries) => {
            this._footerStateReady = true;
            this._footerVisible = Boolean(entries[0]?.isIntersecting);
            this._updateVisibility();
          }, { threshold: 0 });
          this._footerObserver.observe(footer);
        } else {
          this._footerStateReady = true;
        }
      }

      if ('MutationObserver' in window) {
        this._primaryObserver = new MutationObserver(() => {
          const isLegacyLoading = this._primaryButton.classList.contains('is-adding');
          if (isLegacyLoading) this._sawLegacyLoading = true;
          if (this._loading && this._sawLegacyLoading && !isLegacyLoading) this._setLoading(false);
          this._scheduleSync();
        });
        this._primaryObserver.observe(this._primaryButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'data-preorder'],
          childList: true,
          characterData: true,
          subtree: true
        });
        if (this._primaryPrice) {
          this._primaryObserver.observe(this._primaryPrice, {
            childList: true,
            characterData: true,
            subtree: true
          });
        }

        this._bodyObserver = new MutationObserver(() => this._updateVisibility());
        this._bodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }

    _scheduleSync(variant, hasVariantOverride = false) {
      if (hasVariantOverride) {
        this._pendingVariant = variant || null;
        this._hasPendingVariant = true;
      }
      if (this._syncFrame) return;
      this._syncFrame = requestFrame(() => {
        this._syncFrame = 0;
        if (this._destroyed) return;
        if (this._hasPendingVariant) {
          const pendingVariant = this._pendingVariant;
          this._hasPendingVariant = false;
          this._pendingVariant = undefined;
          this._syncVariant(pendingVariant, true);
          return;
        }
        this._syncVariant();
      });
    }

    _syncVariant(variant, hasVariantOverride = false) {
      if (!this._submitButton) return;

      let option = null;
      let available = false;
      if (hasVariantOverride) {
        if (variant) {
          option = this._findVariantOption(variant.id);
          available = Boolean(variant.available) && this._isPrimaryPurchasable();
        }
      } else {
        option = this._variantSelect?.selectedOptions?.[0] || null;
        available = option
          ? option.dataset.variantAvailable === 'true' && this._isPrimaryPurchasable()
          : this._isPrimaryPurchasable();
      }

      const title = option?.dataset.variantTitle || '';
      const price = option?.dataset.variantPrice || this._primaryPrice?.textContent.trim() || '';
      const imageUrl = option?.dataset.variantImage || '';
      const isPreorder = available && this._primaryButton.dataset.preorder === 'true';
      let label = this.dataset.unavailableLabel || 'Unavailable';
      if (available) {
        label = isPreorder
          ? (this.dataset.preorderLabel || 'Pre-order')
          : (this.dataset.addLabel || 'Add to Cart');
      } else if (variant || option) {
        label = this.dataset.soldOutLabel || 'Sold Out';
      }

      if (this._variant) {
        this._variant.textContent = title;
        this._variant.hidden = !title || title === 'Default Title';
      }
      if (this._price) this._price.textContent = price;
      if (this._submitLabel) this._submitLabel.textContent = label;
      this._submitButton.dataset.preorder = String(isPreorder);
      this._submitButton.disabled = this._loading || !available;

      if (this._image && imageUrl && this._image.getAttribute('src') !== imageUrl) {
        this._image.setAttribute('src', imageUrl);
        this._image.removeAttribute('srcset');
        if (this._media) this._media.hidden = false;
      }

      const announcement = [title, price, label].filter(Boolean).join(', ');
      if (this._hasSynced && this._status && announcement !== this._lastAnnouncement) {
        this._status.textContent = announcement;
      }
      this._lastAnnouncement = announcement;
      this._hasSynced = true;
    }

    _isPrimaryPurchasable() {
      return Boolean(this._primaryButton)
        && !this._primaryButton.disabled
        && !this._primaryButton.classList.contains('disabled')
        && !this._primaryButton.classList.contains('d-none')
        && this._primaryButton.getAttribute('aria-disabled') !== 'true';
    }

    _findVariantOption(id) {
      if (!this._variantSelect || id == null) return null;
      return Array.from(this._variantSelect.options).find(
        (option) => String(option.dataset.variantId) === String(id)
      ) || null;
    }

    _setLoading(loading) {
      if (!this._submitButton) return;
      this._loading = Boolean(loading);
      if (this._loading) {
        this._submitButton.setAttribute('aria-busy', 'true');
        this._submitButton.setAttribute('aria-disabled', 'true');
        this._submitButton.disabled = true;
        this._sawLegacyLoading = false;
        window.clearTimeout(this._loadingTimer);
        this._loadingTimer = window.setTimeout(() => this._setLoading(false), 12000);
      } else {
        window.clearTimeout(this._loadingTimer);
        this._submitButton.removeAttribute('aria-busy');
        this._submitButton.removeAttribute('aria-disabled');
        this._scheduleSync();
      }
    }

    _updateVisibility() {
      if (this._destroyed || !this._source) return;
      const inScope = this._scope === 'all' || this._scopeMedia.matches;
      const shouldShow = inScope
        && this._hasPassedSource
        && !this._sourceVisible
        && this._footerStateReady
        && !this._footerVisible
        && document.visibilityState !== 'hidden'
        && !this._isOverlayOpen()
        && !this._isSoftKeyboardOpen();

      if (shouldShow) this._show();
      else this._hide(false);

      if (this._visible) updateDocumentOffset();
    }

    _isOverlayOpen() {
      const body = document.body;
      return body.classList.contains('drawer--open')
        || body.classList.contains('modal-open')
        || body.classList.contains('ajaxcart--is-loading')
        || Boolean(document.querySelector('.js-cart-modal.show, .cart-drawer.show, [data-quickview].show, [data-quickadd].show'));
    }

    _isSoftKeyboardOpen() {
      const viewport = window.visualViewport;
      const activeElement = document.activeElement;
      if (!viewport || !activeElement?.matches?.('input, textarea, select, [contenteditable="true"]')) return false;
      return viewport.height < window.innerHeight * 0.75;
    }

    _show() {
      if (this._visible) return;
      window.clearTimeout(this._hideTimer);
      this._pendingHide = false;
      this._visible = true;
      this.hidden = false;
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      activeBars.add(this);
      updateDocumentOffset();
      this._showFrame = requestFrame(() => {
        if (this._visible && !this._destroyed) this.classList.add('is-visible');
      });
    }

    _hide(immediate = false) {
      if (!immediate && this.contains(document.activeElement)) {
        this._pendingHide = true;
        return;
      }
      this._pendingHide = false;
      this._visible = false;
      this.classList.remove('is-visible');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      activeBars.delete(this);
      updateDocumentOffset();
      window.clearTimeout(this._hideTimer);

      if (immediate || this._reducedMotionMedia?.matches) {
        this.hidden = true;
        return;
      }
      this._hideTimer = window.setTimeout(() => {
        if (!this._visible) this.hidden = true;
      }, 240);
    }

    _addMediaListener(mediaQuery, listener) {
      if (mediaQuery?.addEventListener) mediaQuery.addEventListener('change', listener);
      else mediaQuery?.addListener?.(listener);
    }

    _removeMediaListener(mediaQuery, listener) {
      if (!listener) return;
      if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener('change', listener);
      else mediaQuery?.removeListener?.(listener);
    }
  }

  window.customElements.define(TAG_NAME, YamuriStickyPurchase);
})();
